import {
  IInvoice,
  IInvoiceBuyerDetail,
  IInvoiceBuyerDetailData,
  IInvoiceData,
  IInvoiceItem,
  IInvoiceItemData,
  IInvoiceShippingDetail,
  IInvoiceShippingDetailData,
  InvoiceStateEnum
} from '.';
import { Address, IAddress } from '../address';
import { IBankPaymentInstruction, BankPaymentInstruction } from '../bank';
import { AbstractModel } from '../base';
import { ICurrency, Currency } from '../currency';
import { PaymentPlanAmountTypeEnum } from '../payment-plan';
import { IQuote, IQuoteData, Quote } from '../quote';
import { ITax, Tax } from '../tax';

export class Invoice extends AbstractModel implements IInvoice {
  readonly id?: number;
  readonly number: number;
  readonly due_date: string | null = null;
  readonly csi_order_number: string = '';
  readonly po_number: string = '';
  readonly payment_terms_description: string = '';
  readonly buyer_detail: IInvoiceBuyerDetail;
  readonly shipping_detail: IInvoiceShippingDetail;
  readonly currency: ICurrency;
  readonly amount_type: PaymentPlanAmountTypeEnum =
    PaymentPlanAmountTypeEnum.FixedAmount;
  readonly quotes: (IQuote | number)[] = [];
  readonly bank_instruction: IBankPaymentInstruction | number | null = null;
  readonly state: number = InvoiceStateEnum.Draft;
  readonly total_amount_due: number = 0;
  readonly subtotal_amount: number = 0;
  readonly total_tax_amount: number = 0;
  readonly items: IInvoiceItem[] = [];
  readonly date?: string;

  constructor(data: IInvoiceData) {
    super();

    const requiredFields: (keyof IInvoiceData)[] = [
      'number',
      'buyer_detail',
      'shipping_detail',
      'currency'
    ];

    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }
    if (data.csi_order_number) {
      this.csi_order_number = data.csi_order_number;
    }
    if (data.po_number) {
      this.po_number = data.po_number;
    }
    this.number = data.number!;
    if (data.payment_terms_description) {
      this.payment_terms_description = data.payment_terms_description;
    }
    if (data.amount_type) {
      this.amount_type = data.amount_type;
    }
    if (data.due_date) {
      this.due_date = data.due_date;
    }
    if (data.date) {
      this.date = data.date;
    }
    if (data.quotes) {
      this.quotes = data.quotes.map(quote =>
        typeof quote === 'number' ? quote : new Quote(quote)
      );
    }
    if (data.bank_instruction) {
      this.bank_instruction =
        typeof data.bank_instruction == 'number'
          ? data.bank_instruction!
          : new BankPaymentInstruction(data.bank_instruction!);
    }

    this.buyer_detail = new InvoiceBuyerDetail(data.buyer_detail!);
    this.shipping_detail = new InvoiceShippingDetail(data.shipping_detail!);
    this.currency =
      typeof data.currency === 'number'
        ? data.currency
        : new Currency(data.currency!);

    if (data.state) {
      this.state = data.state;
    }

    if (data.total_amount_due) {
      this.total_amount_due = data.total_amount_due;
    }
    if (data.subtotal_amount) {
      this.subtotal_amount = data.subtotal_amount;
    }
    if (data.total_tax_amount) {
      this.total_tax_amount = data.total_tax_amount;
    }
    if (data.items) {
      this.items = data.items.map(item => new InvoiceItem(item));
    }
  }

  toString() {
    return this.number.toString();
  }

  toCustomerInvoiceString() {
    let str = `${this.toString()}`;

    if (this.buyer_detail) {
      str += ` - ${this.buyer_detail.name}`;
    }

    return str;
  }
}

export class InvoiceBuyerDetail extends AbstractModel
  implements IInvoiceBuyerDetail {
  readonly id?: number;
  readonly name: string;
  readonly customer_number: string = '';
  readonly address: IAddress;

  constructor(data: IInvoiceBuyerDetailData) {
    super();

    const requiredFields: (keyof IInvoiceBuyerDetailData)[] = ['name'];

    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }
    this.name = data.name!;
    if (data.customer_number) {
      this.customer_number = data.customer_number!;
    }
    this.address = new Address(data.address!);
  }
}

export class InvoiceShippingDetail extends AbstractModel
  implements IInvoiceShippingDetail {
  readonly id?: number;
  readonly name: string;
  readonly address: IAddress;

  constructor(data: IInvoiceShippingDetailData) {
    super();

    const requiredFields: (keyof IInvoiceShippingDetailData)[] = ['name'];

    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }
    this.name = data.name!;
    this.address = new Address(data.address!);
  }
}

export class InvoiceItem extends AbstractModel implements IInvoiceItem {
  readonly id?: number;
  readonly description: string;
  readonly proposal?: string;
  readonly term_percentage: string | null = null;
  readonly amount_due: string | null = null;
  readonly calculated_amount_due: number = 0;
  readonly price: string = '0';
  readonly tax: ITax | null = null;
  readonly tax_amount: number = 0;
  readonly tax_name: string = '';
  readonly tax_rate: number | null = null;

  constructor(data: IInvoiceItemData) {
    super();

    const requiredFields: (keyof IInvoiceItemData)[] = ['description'];

    this.validate(data, requiredFields);

    if (data.id) {
      this.id = data.id;
    }
    if (data.calculated_amount_due) {
      this.calculated_amount_due = data.calculated_amount_due;
    }
    this.description = data.description!;

    if (data.proposal) {
      this.proposal = data.proposal;
    }
    if (data.term_percentage) {
      this.term_percentage = data.term_percentage;
    }
    if (data.amount_due) {
      this.amount_due = data.amount_due;
    }
    if (data.price) {
      this.price = data.price;
    }
    if (data.tax) {
      this.tax = new Tax(data.tax);
    }
    if (data.tax_name) {
      this.tax_name = data.tax_name;
    }
    if (data.tax_rate) {
      this.tax_rate = data.tax_rate;
    }
    if (data.tax_amount) {
      this.tax_amount = data.tax_amount;
    }
  }
}
